// ============================================================================
// TEMPLATE CONTEXT
// Shared state for session templates across the application
// ============================================================================

import React, { createContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { SessionTemplate, PhaseTemplate } from '../types';
import { browseSessionTemplates, browsePhaseTemplates } from '../services/templateService';
import { useAuth } from './AuthContext';

interface TemplateContextType {
  // Session Templates
  currentTemplate: SessionTemplate | null;
  sessionTemplates: SessionTemplate[];
  defaultTemplate: SessionTemplate | null;
  loadingSessionTemplates: boolean;
  sessionTemplatesError: string | null;

  // Phase Templates
  phaseTemplates: PhaseTemplate[];
  loadingPhaseTemplates: boolean;
  phaseTemplatesError: string | null;

  // Actions
  setCurrentTemplate: (template: SessionTemplate | null) => void;
  refreshSessionTemplates: () => Promise<void>;
  refreshPhaseTemplates: () => Promise<void>;
  getTemplateById: (templateId: string) => SessionTemplate | undefined;
  getPhaseTemplateById: (phaseTemplateId: string) => PhaseTemplate | undefined;
}

const TemplateContext = createContext<TemplateContextType | undefined>(undefined);

interface TemplateProviderProps {
  children: ReactNode;
}

export function TemplateProvider({ children }: TemplateProviderProps) {
  const { loading: authLoading, accessToken } = useAuth();

  // Session Templates State
  const [currentTemplate, setCurrentTemplate] = useState<SessionTemplate | null>(null);
  const [sessionTemplates, setSessionTemplates] = useState<SessionTemplate[]>([]);
  const [defaultTemplate, setDefaultTemplate] = useState<SessionTemplate | null>(null);
  const [loadingSessionTemplates, setLoadingSessionTemplates] = useState<boolean>(true);
  const [sessionTemplatesError, setSessionTemplatesError] = useState<string | null>(null);

  // Phase Templates State
  const [phaseTemplates, setPhaseTemplates] = useState<PhaseTemplate[]>([]);
  const [loadingPhaseTemplates, setLoadingPhaseTemplates] = useState<boolean>(true);
  const [phaseTemplatesError, setPhaseTemplatesError] = useState<string | null>(null);

  /**
   * Fetch session templates
   */
  const refreshSessionTemplates = useCallback(async () => {
    setLoadingSessionTemplates(true);
    setSessionTemplatesError(null);

    try {
      const result = await browseSessionTemplates({ limit: 100 });
      setSessionTemplates(result.templates);
      const defaultTpl = result.templates.find(t => t.isDefault);
      if (defaultTpl) setDefaultTemplate(defaultTpl);
    } catch (error: any) {
      console.error('Failed to load session templates:', error);
      setSessionTemplatesError(error.message);
    } finally {
      setLoadingSessionTemplates(false);
    }
  }, []);

  const refreshPhaseTemplates = useCallback(async () => {
    setLoadingPhaseTemplates(true);
    setPhaseTemplatesError(null);

    try {
      const result = await browsePhaseTemplates({ limit: 100 });
      setPhaseTemplates(result.templates);
    } catch (error: any) {
      console.error('Failed to load phase templates:', error);
      setPhaseTemplatesError(error.message);
    } finally {
      setLoadingPhaseTemplates(false);
    }
  }, []);

  const getTemplateById = useCallback((templateId: string): SessionTemplate | undefined => {
    return sessionTemplates.find(t => t.sessionTemplateId === templateId);
  }, [sessionTemplates]);

  const getPhaseTemplateById = useCallback((phaseTemplateId: string): PhaseTemplate | undefined => {
    return phaseTemplates.find(pt => pt.phaseTemplateId === phaseTemplateId);
  }, [phaseTemplates]);

  // Load templates once auth has resolved and a token is attached — fetching
  // earlier races the axios interceptor in api.ts and gets a 401.
  useEffect(() => {
    if (authLoading || !accessToken) return;
    refreshSessionTemplates();
    refreshPhaseTemplates();
  }, [authLoading, accessToken]);

  const value = useMemo<TemplateContextType>(() => ({
    currentTemplate,
    sessionTemplates,
    defaultTemplate,
    loadingSessionTemplates,
    sessionTemplatesError,
    phaseTemplates,
    loadingPhaseTemplates,
    phaseTemplatesError,
    setCurrentTemplate,
    refreshSessionTemplates,
    refreshPhaseTemplates,
    getTemplateById,
    getPhaseTemplateById
  }), [currentTemplate, sessionTemplates, defaultTemplate, loadingSessionTemplates, sessionTemplatesError, phaseTemplates, loadingPhaseTemplates, phaseTemplatesError, setCurrentTemplate, refreshSessionTemplates, refreshPhaseTemplates, getTemplateById, getPhaseTemplateById]);

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
}

